import { __assign, __extends, __rest } from "tslib";
import React from 'react';
import PropTypes from 'prop-types';
import IconButton from '../IconButton';
import { avatarShapes, dataHooks } from './constants';
import { Avatar as CoreAvatar } from 'wix-ui-core/dist/es/src/components/avatar';
import { WixStyleReactContext } from '../WixStyleReactProvider/context';
import Loader from '../Loader';
import { placeholderSVGs } from './assets';
import { st, classes } from './Avatar.st.css';
import { capitalize } from '../utils/cssClassUtils';
import stringToColor from './string-to-color';
var getSizeNumber = function (size) { return Number(size.substring(4)); };
var defaultSize = 48;
var minSmallIconButton = 60;
/**
 * Avatar is a type of element that visually represents a user, either as an image, name initials or placeholder icon.
 */
var Avatar = /** @class */ (function (_super) {
    __extends(Avatar, _super);
    function Avatar(props) {
        var _this = _super.call(this, props) || this;
        _this._onMouseEnter = function () {
            if (_this.props.showIndicationOnHover) {
                _this.setState({ showIndication: true });
            }
        };
        _this._onMouseLeave = function () {
            if (_this.props.showIndicationOnHover) {
                _this.setState({ fadeIndication: true });
                setTimeout(function () { return _this.setState({ fadeIndication: false, showIndication: false }); }, 150);
            }
        };
        _this.state = {
            fadeIndication: false,
            showIndication: false,
        };
        return _this;
    }
    Avatar.prototype.render = function () {
        var _this = this;
        var _a = this.props, size = _a.size, presence = _a.presence, indication = _a.indication, color = _a.color, customIndication = _a.customIndication, onIndicationClick = _a.onIndicationClick, dataHook = _a.dataHook, className = _a.className, shape = _a.shape, text = _a.text, placeholder = _a.placeholder, name = _a.name, onClick = _a.onClick, showIndicationOnHover = _a.showIndicationOnHover, loading = _a.loading, rest = __rest(_a, ["size", "presence", "indication", "color", "customIndication", "onIndicationClick", "dataHook", "className", "shape", "text", "placeholder", "name", "onClick", "showIndicationOnHover", "loading"]);
        var _b = this.state, fadeIndication = _b.fadeIndication, showIndication = _b.showIndication;
        var calculatedColor = color || stringToColor(text || name); // if color is provided as a prop use it, otherwise, generate a color based on the text
        var sizeNumber = getSizeNumber(size);
        var renderOnHover = !showIndicationOnHover || showIndication;
        var indicationConstraints = renderOnHover && sizeNumber >= defaultSize;
        var renderIndication = indicationConstraints && !customIndication && indication;
        var renderCustomIndication = indicationConstraints && customIndication;
        var renderLoader = loading && sizeNumber >= defaultSize;
        return (React.createElement(WixStyleReactContext.Consumer, null, function (_a) {
            var newColorsBranding = _a.newColorsBranding;
            return (React.createElement("div", { "data-hook": dataHook, className: st(className, classes.root) },
                React.createElement("div", { "data-hook": dataHooks.avatarWSR, onMouseEnter: _this._onMouseEnter, onMouseLeave: _this._onMouseLeave, className: st(classes.avatarContainer, {
                        shape: shape,
                        size: size,
                        indication: Boolean(customIndication || indication),
                        presence: Boolean(presence),
                        presenceType: presence,
                        clickable: !!onClick,
                        fade: fadeIndication,
                        hasText: !!text,
                        newColorsBranding: newColorsBranding,
                    }) },
                    React.createElement("div", { className: classes.coreAvatar },
                        React.createElement(CoreAvatar, __assign({}, __assign(__assign({}, rest), { placeholder: placeholder ? (placeholder) : (React.createElement(AvatarDefaultPlaceholder, { shape: shape, size: size })), text: text, name: name, onClick: onClick, initialsLimit: sizeNumber < 30 ? 1 : undefined, 'data-hook': dataHooks.avatarCore }), { className: st(classes.avatar, classes["color".concat(capitalize(calculatedColor))]) }))),
                    renderLoader && [
                        React.createElement("div", { key: "overlay", className: st(classes.loaderContainer, classes.overlay) }),
                        React.createElement("div", { key: "loader", className: st(classes.loaderContainer, classes.loader) },
                            React.createElement(Loader, { dataHook: dataHooks.loader, size: "tiny" })),
                    ],
                    presence && React.createElement("div", { className: classes.presence }),
                    renderIndication && (React.createElement("div", { className: classes.indication },
                        React.createElement(IconButton, { className: classes.iconButtonShadow, dataHook: dataHooks.indication, onClick: onIndicationClick, skin: "inverted", shape: shape, size: sizeNumber > minSmallIconButton ? 'small' : 'tiny' }, indication))),
                    renderCustomIndication && (React.createElement("div", { className: classes.indication, "data-hook": dataHooks.customIndication, onClick: onIndicationClick }, customIndication)))));
        }));
    };
    return Avatar;
}(React.PureComponent));
var AvatarDefaultPlaceholder = function (_a) {
    var shape = _a.shape, size = _a.size;
    return shape !== avatarShapes.square
        ? placeholderSVGs[size][avatarShapes.circle]
        : placeholderSVGs[size][avatarShapes.square];
};
var CoreAvatarPropTypes = {
    /**
     * Defines a name of the avatar user. Text initials will be generated from the name.
     * The name value will be used as default value for HTML title and `aria-label` attributes.
     * And also as default value for the image's alt attribute if `imgProps` is provided.
     */
    name: PropTypes.string,
    /** Defines a text to render as a content instead of a given `name`. */
    text: PropTypes.string,
    /**
     * Accept any content to render as a content placeholder. This placeholder will be displayed if no text, name or imgProps are provided.<br>
     * By default use a generic avatar user icon.
     */
    placeholder: PropTypes.node,
    /** Accept all common `<img>` tag properties. */
    imgProps: PropTypes.object,
    /** Defines a string that labels the current element in case where text label is not visible on the screen. */
    ariaLabel: PropTypes.string,
    /** Defines a standard HTML title attribute value. Applies it to the root element. */
    title: PropTypes.string,
    /** Defines a click event handler. When defined, component will be clickable and will have a pointer cursor on hover. */
    onClick: PropTypes.func,
};
Avatar.displayName = 'Avatar';
Avatar.propTypes = __assign(__assign({}, CoreAvatarPropTypes), { 
    /** Controls the size of an avatar. */
    size: PropTypes.oneOf([
        'size90',
        'size72',
        'size60',
        'size48',
        'size36',
        'size30',
        'size24',
        'size18',
    ]), 
    /**
     * Controls the background color of the avatar. If not provided,
     * color is determined by the provided text or name so that each name will receive a different color.
     */
    color: PropTypes.oneOf(['A1', 'A2', 'A3', 'A4', 'A5', 'A6']), 
    /** Controls the shape of the image. */
    shape: PropTypes.oneOf(['circle', 'square']), 
    /** Specifies a CSS class name to be appended to the component’s root element. */
    className: PropTypes.string, 
    /** Applies a data-hook HTML attribute that can be used in the tests. */
    dataHook: PropTypes.string, 
    /** Sets the presence status of an avatar. */
    presence: PropTypes.oneOf(['online', 'offline', 'busy']), 
    /** Accept any content to render as an indication item. */
    indication: PropTypes.node, 
    /**
     * Accept any content to render as a custom indication item. This indication element will not be wrapped by an IconButton.
     * It could be rendered in other shapes (such as square).
     * */
    customIndication: PropTypes.node, 
    /** Defines a callback function which is called every time indication element is clicked. */
    onIndicationClick: PropTypes.func, 
    /** Shows indication element on hover. */
    showIndicationOnHover: PropTypes.bool, 
    /** Shows a loader on top of an avatar. */
    loading: PropTypes.bool });
Avatar.defaultProps = {
    size: 'size48',
    shape: 'circle',
    showIndicationOnHover: false,
};
export default Avatar;
//# sourceMappingURL=Avatar.js.map